import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, RotateCcw, Calculator, Trophy, Printer } from "lucide-react";

// Conversion factors to base unit (kg or L)
const UNIT_TO_BASE = {
  kg: 1, g: 0.001,
  L: 1, ml: 0.001,
  // For "each" we just divide price by count → R per unit
  each: 1,
};

const UNIT_GROUPS = {
  weight: ["kg", "g"],
  volume: ["L", "ml"],
  count: ["each"],
};

const BASE_LABEL = { weight: "kg", volume: "L", count: "unit" };

const blankRow = () => ({
  id: crypto.randomUUID(),
  name: "",
  price: "",
  size: "",
  unit: "kg",
});

const fmtR = (n) =>
  isFinite(n) && n > 0
    ? `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

export default function UnitPriceCalculator() {
  const [comparisonType, setComparisonType] = useState("weight"); // weight | volume | count
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow()]);
  const [title, setTitle] = useState("");

  const handlePrint = () => {
    // Build a fresh window so the host page's React + Emergent overlay don't
    // interfere with the print output.
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    const safeTitle = (title || "Unit Price Comparison").replace(/[<>]/g, "");
    const dateStr = new Date().toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
    const validRows = computed.filter((r) => r.pricePerBase > 0);
    const ranked = [...validRows].sort((a, b) => a.pricePerBase - b.pricePerBase);
    const cheapest = ranked.length ? ranked[0].pricePerBase : 0;

    const rowsHtml = ranked.length
      ? ranked.map((r, i) => {
          const pctOver = i === 0 ? "CHEAPEST" : `+${((r.pricePerBase / cheapest - 1) * 100).toFixed(1)}%`;
          return `
            <tr class="${i === 0 ? "winner" : ""}">
              <td class="num">${i + 1}</td>
              <td>${(r.name || `Item ${i + 1}`).replace(/[<>]/g, "")}</td>
              <td class="num">R ${Number(r.price).toFixed(2)}</td>
              <td class="num">${Number(r.size).toFixed(2)} ${r.unit}</td>
              <td class="num strong">${fmtR(r.pricePerBase).replace("R ", "R ")}</td>
              <td class="num ${i === 0 ? "good" : "muted"}">${pctOver}</td>
            </tr>`;
        }).join("")
      : `<tr><td colspan="6" style="text-align:center;color:#888;padding:24px">No valid rows to print — fill in price and size first.</td></tr>`;

    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>${safeTitle}</title>
  <style>
    @page { size: A4 portrait; margin: 18mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; }
    h1 { font-size: 22px; margin: 0 0 4px 0; }
    .sub { color: #666; font-size: 12px; margin-bottom: 16px; }
    .mode { display: inline-block; background: #eef2ff; color: #3730a3; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; margin-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border-bottom: 1px solid #ddd; padding: 8px 10px; text-align: left; }
    th { background: #f8fafc; text-transform: uppercase; font-size: 11px; color: #555; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .winner { background: #ecfdf5; }
    .winner td { font-weight: 600; }
    .good { color: #047857; font-weight: 700; }
    .muted { color: #6b7280; }
    .strong { font-weight: 700; }
    .footer { margin-top: 28px; font-size: 10px; color: #888; }
  </style>
</head>
<body>
  <div class="mode">${comparisonType.toUpperCase()} — Price per ${baseUnit}</div>
  <h1>${safeTitle}</h1>
  <div class="sub">Generated ${dateStr}</div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Product</th>
        <th class="num">Price</th>
        <th class="num">Size</th>
        <th class="num">Price / ${baseUnit}</th>
        <th class="num">% over cheapest</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  <div class="footer">Bosveld Bou &amp; Hardware — Unit Price Comparator</div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };<\/script>
</body>
</html>`);
    w.document.close();
  };

  const baseUnit = BASE_LABEL[comparisonType];
  const allowedUnits = UNIT_GROUPS[comparisonType];

  const computed = useMemo(() => {
    return rows.map((r) => {
      const price = Number(r.price) || 0;
      const size = Number(r.size) || 0;
      const factor = UNIT_TO_BASE[r.unit] ?? 1;
      const sizeInBase = size * factor;
      const pricePerBase = price > 0 && sizeInBase > 0 ? price / sizeInBase : 0;
      return { ...r, pricePerBase };
    });
  }, [rows]);

  // Rankings (skip rows with no price-per-base)
  const valid = computed.filter((r) => r.pricePerBase > 0);
  const cheapest = valid.length ? Math.min(...valid.map((r) => r.pricePerBase)) : 0;
  const mostExpensive = valid.length ? Math.max(...valid.map((r) => r.pricePerBase)) : 0;

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, blankRow()]);
  const removeRow = (id) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  const reset = () => setRows([blankRow(), blankRow(), blankRow()]);

  const handleTypeChange = (newType) => {
    setComparisonType(newType);
    // Reset units in each row to the first allowed for that group
    setRows((prev) => prev.map((r) => ({ ...r, unit: UNIT_GROUPS[newType][0] })));
  };

  // Sorted ranking display
  const ranked = [...valid].sort((a, b) => a.pricePerBase - b.pricePerBase);

  return (
    <div className="space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="h-7 w-7 text-blue-600" />
            Unit Price Comparator
          </h1>
          <p className="text-sm text-gray-500">
            Compare products by price-per-kg, price-per-litre, or price-per-unit. Cheapest option highlighted automatically.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input
            placeholder="Title (optional, e.g. Coke sizes Feb 2026)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-64"
            data-testid="upc-title"
          />
          <Button variant="outline" onClick={handlePrint} disabled={valid.length === 0} data-testid="upc-print">
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          <Button variant="outline" onClick={reset} data-testid="upc-reset">
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
          <Button onClick={addRow} data-testid="upc-add-row">
            <Plus className="h-4 w-4 mr-1" />
            Add row
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Comparison mode</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={comparisonType === "weight" ? "default" : "outline"}
              onClick={() => handleTypeChange("weight")}
              data-testid="upc-mode-weight"
            >
              Weight (R / kg)
            </Button>
            <Button
              variant={comparisonType === "volume" ? "default" : "outline"}
              onClick={() => handleTypeChange("volume")}
              data-testid="upc-mode-volume"
            >
              Volume (R / L)
            </Button>
            <Button
              variant={comparisonType === "count" ? "default" : "outline"}
              onClick={() => handleTypeChange("count")}
              data-testid="upc-mode-count"
            >
              Count (R / unit)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Input rows */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {computed.map((r, idx) => {
            const isCheapest = r.pricePerBase > 0 && r.pricePerBase === cheapest && valid.length > 1;
            const isExpensive = r.pricePerBase > 0 && r.pricePerBase === mostExpensive && valid.length > 1;
            const bg = isCheapest
              ? "bg-emerald-50 border-emerald-300"
              : isExpensive
              ? "bg-red-50 border-red-200"
              : "border-gray-200";
            return (
              <div
                key={r.id}
                className={`grid grid-cols-12 gap-2 items-end p-3 rounded-lg border-2 ${bg}`}
                data-testid={`upc-row-${idx}`}
              >
                <div className="col-span-3 space-y-1">
                  <Label className="text-xs">Product name</Label>
                  <Input
                    placeholder={idx === 0 ? "e.g. Bobtail Beef 8kg" : "Product..."}
                    value={r.name}
                    onChange={(e) => updateRow(r.id, { name: e.target.value })}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Price (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={r.price}
                    onChange={(e) => updateRow(r.id, { price: e.target.value })}
                    data-testid={`upc-price-${idx}`}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Size</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={r.size}
                    onChange={(e) => updateRow(r.id, { size: e.target.value })}
                    data-testid={`upc-size-${idx}`}
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Select value={r.unit} onValueChange={(v) => updateRow(r.id, { unit: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedUnits.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 text-right">
                  <Label className="text-xs">Price / {baseUnit}</Label>
                  <div
                    className={`text-lg font-bold font-mono mt-1 ${
                      isCheapest ? "text-emerald-700" : isExpensive ? "text-red-600" : "text-gray-700"
                    }`}
                    data-testid={`upc-result-${idx}`}
                  >
                    {fmtR(r.pricePerBase)}
                    {isCheapest && (
                      <Badge className="ml-2 bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]">
                        BEST
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeRow(r.id)}
                    disabled={rows.length <= 1}
                    title="Remove row"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Ranking summary */}
      {ranked.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              Ranked cheapest first
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2">#</th>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-right px-4 py-2">Price / {baseUnit}</th>
                  <th className="text-right px-4 py-2">% more than cheapest</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((r, i) => {
                  const pctOver = i === 0 ? 0 : ((r.pricePerBase / cheapest - 1) * 100);
                  return (
                    <tr key={r.id} className={i === 0 ? "bg-emerald-50" : ""}>
                      <td className="px-4 py-2 font-bold text-gray-500">{i + 1}</td>
                      <td className="px-4 py-2">{r.name || `Item ${i + 1}`}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{fmtR(r.pricePerBase)}</td>
                      <td className={`px-4 py-2 text-right font-mono ${i === 0 ? "text-emerald-700 font-bold" : "text-gray-600"}`}>
                        {i === 0 ? "CHEAPEST" : `+${pctOver.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
